import ProjectSchedule from "./Schedule"
import Resources from "./Resources"

import { Card, CardContent, Grid, makeStyles, Theme, Typography, CardHeader } from "@material-ui/core"
import { COLOR_GRAY } from "../../../constants"
import { ProjectDashboardViewModel } from "../../api/project-dashboard"
import { useTranslations } from "./useTranslations"
import { Status } from "../../../types/common"

const ProjectOverview = ({ projectDashboard }: ProjectOverviewProps) => {
  if (projectDashboard.isPersonalProject) {
    return <PersonalProjectOverview projectDashboard={projectDashboard} />
  } else {
    return <OrgProjectOverview projectDashboard={projectDashboard} />
  }
}

const OrgProjectOverview = ({ projectDashboard }: ProjectOverviewProps) => {
  const classes = useStyles()
  const translations = useTranslations()

  return (
    <>
      <section className={classes.section}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined" className={classes.card}>
              <CardHeader title={translations.customerCardHeader} className={classes.cardHeader} disableTypography />
              <CardContent className={classes.cardContent}>
                <Typography variant="h4">{projectDashboard.projectCustomers.join(",")}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined" className={classes.card}>
              <CardHeader
                title={translations.currentStatusCardHeader}
                className={classes.cardHeader}
                disableTypography
              />
              <CardContent className={classes.cardContent}>
                <Typography variant="h4">
                  {getStatusTranslation(projectDashboard.projectStatus, translations)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined" className={classes.card}>
              <CardHeader
                title={translations.latestStatusDescriptionCardHeader}
                className={classes.cardHeader}
                disableTypography
              />
              <CardContent className={classes.cardContent}>
                <Typography variant="h4">{projectDashboard.latestShortStatusDescription}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </section>

      <section className={classes.section}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <ProjectSchedule projectDashboard={projectDashboard} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Resources projectDashboard={projectDashboard} />
          </Grid>
        </Grid>
      </section>
    </>
  )
}

const PersonalProjectOverview = ({ projectDashboard }: ProjectOverviewProps) => {
  const classes = useStyles()
  const translations = useTranslations()

  return (
    <>
      <section className={classes.section}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Grid container direction="row" className={classes.container}>
              <Grid item xs={12} className={classes.gridItem}>
                <Card variant="outlined" className={classes.card}>
                  <CardHeader
                    title={translations.currentStatusCardHeader}
                    className={classes.cardHeader}
                    disableTypography
                  />
                  <CardContent className={classes.cardContent}>
                    <Typography variant="h4">
                      {getStatusTranslation(projectDashboard.projectStatus, translations)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} className={classes.gridItem}>
                <Card variant="outlined" className={classes.card}>
                  <CardHeader
                    title={translations.latestStatusDescriptionCardHeader}
                    className={classes.cardHeader}
                    disableTypography
                  />
                  <CardContent className={classes.cardContent}>
                    <Typography variant="h4">{projectDashboard.latestShortStatusDescription}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6}>
            <ProjectSchedule projectDashboard={projectDashboard} />
          </Grid>
        </Grid>
      </section>
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    height: "100%",
  },
  gridItem: {
    display: "flex",
    flexDirection: "column",
    paddingBottom: theme.spacing(1),
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  section: {
    marginBottom: theme.spacing(1),
  },
  card: {
    height: "100%",
    flex: 1,
  },
  cardHeader: {
    color: COLOR_GRAY,
    fontSize: "1rem",
    paddingBottom: 0,
  },
  cardContent: {
    "& h6": {
      fontWeight: 400,
    },
    paddingTop: 0,
    whiteSpace: "pre-line",
  },
}))

function getStatusTranslation(status: string, translations: any) {
  let statusTranslation = status
  if (status === Status.NOT_STARTED) statusTranslation = translations.notStarted
  if (status === Status.IN_PROGRESS) statusTranslation = translations.inProgress
  if (status === Status.COMPLETED) statusTranslation = translations.completed
  if (status === Status.REJECTED) statusTranslation = translations.rejected
  return statusTranslation
}

export default ProjectOverview
type ProjectOverviewProps = {
  projectDashboard: ProjectDashboardViewModel
}
